import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import type { OtherWikiPartsFragment } from '~/apollo/generated/v4/graphql';
import { Panel } from '~/components/common/Panel';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { OtherWikiFormFields } from '~/components/upload/other-wiki/other-wiki-form-fields';
import { useZodForm } from '~/utils/forms';
import { initialOtherWiki, otherWikiSchema } from '~/utils/modules/other-wiki';

export const UPDATE_OTHER_WIKI = graphql(`
  mutation UpdateOtherWiki($id: ID!, $input: OtherWikiUpdateInput!) {
    otherWikiUpdate(id: $id, input: $input) {
      result {
        ...otherWikiParts
      }
    }
  }
`);

export function UpdateOtherWikiForm({
  otherWiki,
}: {
  otherWiki: OtherWikiPartsFragment;
}) {
  const [updateOtherWiki, { loading, error }] = useMutation(UPDATE_OTHER_WIKI);

  const form = useZodForm({
    schema: otherWikiSchema,
    values: initialOtherWiki(otherWiki),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateOtherWiki({
        variables: {
          id: parseInt(otherWiki.id),
          input: values,
        },
      });
      toast.success('Other wiki page updated successfully.');
    } catch (err) {
      console.log('Error updating other wiki page', err);
      toast.error('There was a problem updating the other wiki page.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <Panel>
          <Panel.Heading>
            <Panel.Title>Update Other Wiki</Panel.Title>
          </Panel.Heading>

          <Panel.Body className="space-y-4">
            <OtherWikiFormFields />
            <HookFormErrors graphQLError={error} />
          </Panel.Body>

          <Panel.Footer align="right">
            <Button type="submit" color="primary" loading={loading}>
              Save
            </Button>
          </Panel.Footer>
        </Panel>
      </form>
    </FormProvider>
  );
}
